import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BRANDS, PARENT_BRAND } from "../../data/content";
import { FadeUp, SectionHead, ChapterTag } from "./Reveal";

function BrandMark({ brand, dark = false }) {
    if (brand.logo) {
        return (
            <img
                src={brand.logo}
                alt={`${brand.name} — official logo`}
                loading="lazy"
                className={`h-12 w-auto object-contain lg:h-14 ${dark ? "" : "mix-blend-multiply"}`}
            />
        );
    }
    return (
        <span className={`font-serif text-2xl font-medium tracking-wide lg:text-[1.7rem] ${dark ? "text-ivory" : "text-navy-deep"}`}>
            {brand.name}
        </span>
    );
}

export default function Brands() {
    const navigate = useNavigate();

    return (
        <section id="brands" className="scroll-mt-20 bg-ivory py-24 text-navy-deep lg:py-36" data-testid="brands-section">
            <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                <ChapterTag light>Chapter 04 &mdash; Our Brands</ChapterTag>
                <SectionHead
                    light
                    className="mt-8"
                    eyebrow="One International Group"
                    title="Specialist Services. One International Group."
                    copy="Each brand serves a clear specialist need while benefiting from the international network, experience and coordination of Viet Medica Ltd."
                />

                <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                    {BRANDS.map((b, i) => (
                        <FadeUp key={b.id} delay={(i % 3) * 0.08} className="h-full">
                            <article
                                data-testid={`brand-card-${b.id}`}
                                className="group flex h-full flex-col border border-navy-deep/10 bg-white p-7 transition-all duration-500 hover:border-gold hover:shadow-[0_24px_60px_-24px_rgba(8,18,37,0.25)] lg:p-8"
                            >
                                <div className="flex h-16 items-center">
                                    <BrandMark brand={b} />
                                </div>
                                <span className="mt-3 text-[10px] font-semibold uppercase tracking-[0.26em] text-navy-deep/45">
                                    A Viet Medica Ltd Brand
                                </span>
                                <p className="mt-3 text-sm leading-relaxed text-navy-deep/70">{b.copy}</p>
                                <div className="mt-auto flex flex-wrap items-center gap-5 pt-7">
                                    <button
                                        type="button"
                                        data-testid={`brand-cta-${b.id}`}
                                        onClick={() => navigate(`/brands/${b.id}`)}
                                        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-navy-deep transition-colors group-hover:text-gold"
                                    >
                                        Learn More
                                        <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                    </button>
                                    {b.website ? (
                                        <a
                                            href={b.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`brand-website-${b.id}`}
                                            className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-deep/45 transition-colors hover:text-gold"
                                        >
                                            Website
                                        </a>
                                    ) : (
                                        <span
                                            data-testid={`brand-website-${b.id}`}
                                            className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-deep/35"
                                        >
                                            Website Coming Soon
                                        </span>
                                    )}
                                </div>
                            </article>
                        </FadeUp>
                    ))}
                </div>

                <FadeUp delay={0.1} className="mt-5">
                    <article
                        data-testid="brand-card-viet-medica"
                        className="group flex flex-col items-start gap-8 border border-gold/30 bg-navy-deep p-8 lg:flex-row lg:items-center lg:justify-between lg:p-12"
                    >
                        <div className="flex items-center gap-7">
                            <span className="inline-flex rounded-[3px] bg-ivory px-4 py-3">
                                <img
                                    src={PARENT_BRAND.logo}
                                    alt="Viet Medica Ltd — official logo"
                                    loading="lazy"
                                    className="h-12 w-auto lg:h-14"
                                />
                            </span>
                            <p className="max-w-xl text-sm leading-relaxed text-slate-300/85 lg:text-base">
                                {PARENT_BRAND.copy}
                            </p>
                        </div>
                        <button
                            type="button"
                            data-testid="brand-cta-viet-medica"
                            onClick={() => navigate("/about")}
                            className="inline-flex shrink-0 items-center gap-2 border border-gold/60 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy-deep"
                        >
                            {PARENT_BRAND.cta}
                            <ArrowUpRight size={14} />
                        </button>
                    </article>
                </FadeUp>
            </div>
        </section>
    );
}
